import { useEffect, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useCatalogs } from '../api/hooks';
import { useAppStore } from '../store/appStore';
import type { CatalogResponse } from '../types';

interface UseActiveCatalogResult {
  /** The catalog the user is currently scoped to, or undefined while the
   *  list is loading or the user has no catalogs. */
  active: CatalogResponse | undefined;
  /** Full catalog list (passes through useCatalogs query data). Exposed so
   *  the picker UI can render all options without a second query. */
  catalogs: CatalogResponse[] | undefined;
  /** Mirrors useCatalogs().isLoading so callers can distinguish "still
   *  resolving" from "no catalogs in this org." */
  isLoading: boolean;
  /** Persist a new selection. The hook re-resolves on the next render. */
  setActive: (id: string) => void;
}

/**
 * Resolves the currently-active catalog from the persisted store id against
 * the catalog list. Falls back to the most recent catalog (catalogs[0]) when:
 *
 *  - no id is stored (first visit, post-logout, post-localStorage-clear)
 *  - the stored id doesn't match any catalog in the user's org (catalog
 *    deleted from another tab, user switched orgs, etc.)
 *
 * This is the single source of truth for "which catalog am I looking at?"
 * across every surface that previously did `catalogs?.[0]`.
 *
 * SC-2012: also syncs URL → store when the route carries a catalog id.
 * Browser back/forward and direct deep links bypass the picker's
 * setActiveCatalogId path, so the sidebar (which reads the store) used to
 * disagree with the page content (which reads useParams). `useMatch` works
 * from any component — including SidebarNav, which lives outside <Routes>
 * and can't call useParams.
 */
export function useActiveCatalog(): UseActiveCatalogResult {
  const { data: catalogs, isLoading } = useCatalogs();
  const activeCatalogId = useAppStore((s) => s.activeCatalogId);
  const setActiveCatalogId = useAppStore((s) => s.setActiveCatalogId);

  const routeMatch = useMatch('/catalogs/:id/*');
  const urlCatalogId = routeMatch?.params.id;

  useEffect(() => {
    // Only sync when the URL carries an id AND it matches a known catalog.
    // Unknown ids (cross-org links, deleted catalogs) shouldn't pollute the
    // store — let the fallback chain below resolve to catalogs[0] instead.
    if (!urlCatalogId || !catalogs) return;
    const isKnown = catalogs.some((c) => c.id === urlCatalogId);
    if (isKnown && urlCatalogId !== activeCatalogId) {
      setActiveCatalogId(urlCatalogId);
    }
  }, [urlCatalogId, catalogs, activeCatalogId, setActiveCatalogId]);

  const active = useMemo<CatalogResponse | undefined>(() => {
    if (!catalogs || catalogs.length === 0) return undefined;
    if (activeCatalogId) {
      const match = catalogs.find((c) => c.id === activeCatalogId);
      if (match) return match;
    }
    return catalogs[0];
  }, [catalogs, activeCatalogId]);

  return {
    active,
    catalogs,
    isLoading,
    setActive: setActiveCatalogId,
  };
}
